import type { ReactNode } from 'react';
import { ProfileCard } from '@/components/common/ProfileCard';
import { ProfileFact, ProfileFacts } from '@/components/common/ProfileFacts';
import { ProfileChip, ProfileChipList } from '@/components/common/ProfileChip';
import { FUNDING_STAGE_OPTIONS } from '@/lib/constants/funding-stages';
import { PROFILE_LAYOUT_STRINGS as L } from '@/lib/constants/profile-layout-strings';
import { UNDERREPRESENTED_CATEGORY_OPTIONS } from '@/lib/constants/underrepresented-categories';
import type { InvestorProfileData } from '../../api/investor-profile.api';
import {
  INVESTOR_PREF_LEVEL_OPTIONS,
  MARKET_MODEL_FIELDS,
  MARKET_MODEL_OPTIONS,
  RECURRING_REVENUE_OPTIONS,
  labelFor,
} from './investor-options';
import { INVESTOR_CARD_STRINGS as C } from './investor-form.constants';

export interface InvestorCardProps {
  data: InvestorProfileData;
  /** The card's Edit button; omitted for read-only viewers (FR-169). */
  action?: ReactNode;
  /** Opens this card's panel from an empty state; omitted when read-only. */
  onAdd?: () => void;
}

/** A small muted heading over a group inside a card (mockup: 12px, 8px below). */
function GroupLabel({ children }: { children: ReactNode }) {
  return <p className="text-subtext mb-2 text-xs">{children}</p>;
}

function ChipGroup({ label, items }: { label: string; items: string[] }) {
  return (
    <div>
      <GroupLabel>{label}</GroupLabel>
      {items.length > 0 ? (
        <ProfileChipList items={items} />
      ) : (
        <p className="text-subtext text-sm font-medium">{L.EMPTY_STAT}</p>
      )}
    </div>
  );
}

/** Only / Sometimes / Never as one grid of dots, one row per market model (FR-175). */
function MarketModelGrid({ data }: { data: InvestorProfileData }) {
  return (
    <table className="w-full table-fixed text-sm">
      <caption className="sr-only">{C.MARKET_MODELS_LABEL}</caption>
      <thead>
        <tr className="text-subtext text-xs">
          <th scope="col" className="w-20 py-[9px] text-left font-normal">
            {C.MARKET}
          </th>
          {MARKET_MODEL_OPTIONS.map((option) => (
            <th key={option.value} scope="col" className="py-[9px] font-normal">
              {option.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {MARKET_MODEL_FIELDS.map(({ name, label }) => (
          <tr key={name} className="border-divider border-t">
            <th scope="row" className="text-navy py-[9px] text-left font-normal">
              {label}
            </th>
            {MARKET_MODEL_OPTIONS.map((option) => {
              const selected = data[name] === option.value;
              return (
                <td key={option.value} className="py-[9px] text-center">
                  <span
                    aria-hidden="true"
                    className={`inline-block size-3 rounded-full ${selected ? 'bg-dkgreen' : 'bg-dot-off'}`}
                  />
                  {selected && <span className="sr-only">{option.label}</span>}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * What we invest in (mockup: 7 of 12 columns): industries, stages and other
 * criteria on the left, the market-model grid on the right. Ticket size,
 * minimum revenue and lead or follow sit in the header strip.
 */
export function WhatWeInvestInCard({ data, action }: InvestorCardProps) {
  const stages = (data.preferredStages ?? []).map(
    (stage) => labelFor(FUNDING_STAGE_OPTIONS, stage) ?? stage,
  );
  const recurring = labelFor(RECURRING_REVENUE_OPTIONS, data.prefRecurringRevenue);
  return (
    <ProfileCard title={C.WHAT_WE_INVEST_IN} action={action}>
      <div className="flex flex-wrap gap-8">
        <div className="flex min-w-0 flex-[1_1_16.25rem] flex-col gap-[1.125rem]">
          <ChipGroup label={C.INDUSTRIES} items={data.industries ?? []} />
          <ChipGroup label={C.STAGES} items={stages} />
          {recurring && (
            <div>
              <p className="text-subtext mb-1 text-xs">{C.RECURRING_REVENUE}</p>
              <p className="text-navy text-sm leading-[1.3125rem]">{recurring}</p>
            </div>
          )}
          <div>
            <p className="text-subtext mb-1 text-xs">{C.OTHER_CRITERIA}</p>
            <p className="text-navy text-sm leading-[1.3125rem]">{data.prefOtherCriteria || L.EMPTY_STAT}</p>
          </div>
        </div>
        <div className="min-w-0 flex-[1_1_16.25rem]">
          <MarketModelGrid data={data} />
        </div>
      </div>
    </ProfileCard>
  );
}

/** Founder preferences (mockup: 5 of 12 columns, one fact per row). */
export function FounderPreferencesCard({ data, action }: InvestorCardProps) {
  const groups = (data.prefUnderrepresentedCategories ?? []).map(
    (value) => labelFor(UNDERREPRESENTED_CATEGORY_OPTIONS, value) ?? value,
  );
  const community: string[] = [];
  if (data.interestOfficeHours) community.push(C.OFFICE_HOURS);
  if (data.interestFounderFunderEvents) community.push(C.FOUNDER_FUNDER_EVENTS);
  return (
    <ProfileCard title={C.FOUNDER_PREFERENCES} action={action}>
      <ProfileFacts columns={1}>
        <ProfileFact
          label={C.FEMALE_LED}
          value={labelFor(INVESTOR_PREF_LEVEL_OPTIONS, data.prefFemaleLed)}
        />
        <ProfileFact
          label={C.UNDERREPRESENTED}
          value={labelFor(INVESTOR_PREF_LEVEL_OPTIONS, data.prefUnderrepresented)}
        />
        {groups.length > 0 && (
          <ProfileFact label={C.UNDERREPRESENTED_GROUPS}>
            <div className="mt-1.5">
              <ProfileChipList items={groups} />
            </div>
          </ProfileFact>
        )}
        <ProfileFact label={C.COMMUNITY}>
          {community.length > 0 ? (
            <div className="mt-1.5 flex flex-wrap gap-2">
              {community.map((item) => (
                <ProfileChip key={item} tone="green">
                  {item}
                </ProfileChip>
              ))}
            </div>
          ) : undefined}
        </ProfileFact>
      </ProfileFacts>
    </ProfileCard>
  );
}
